import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Spacer } from "../offering/spacer";
import { PulseLoader } from "../components/pulse_loader";
import { Container } from "../components";
import { CheckoutInput } from "../offering/checkout";
import { countries } from "../offering/countries";
import * as stripeJs from "@stripe/stripe-js";
import { useAppleGooglePayEmbedInCheckoutPage } from "./splitio";
import { Button } from "react-bootstrap";
import { useI18n } from "../lib/i18n/i18n_provider";
import { Quote } from "../offering/api";
import { useAnalytics } from "@/lib/analytics/analytics";

export interface PaymentElementInputProps {
  quote?: Quote;
  paymentProcessing: boolean;
  onProcessing: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  redirectUrl: string;
  billingInfo: CheckoutInput;
  onChangePaymentType: (paymentType: string) => void;
  setFieldValue: (field: string, value: any) => void;
  partnerId: string;
  showConfirmButton?: boolean;
  paymentSessionExpired?: boolean;
}

export interface PaymentElementInputActions {
  submitPayment: (quotePositivePrice?: boolean) => Promise<void>;
}

const PaymentElementInput = forwardRef(
  (props: PaymentElementInputProps, ref) => {
    const {
      quote,
      partnerId,
      redirectUrl,
      onSuccess,
      onError,
      onProcessing,
      billingInfo,
      setFieldValue,
      paymentProcessing,
      onChangePaymentType,
      showConfirmButton,
      paymentSessionExpired,
    } = props;

    const analytics = useAnalytics();
    const isGoogleApplePayEmbedInCheckoutPage =
      useAppleGooglePayEmbedInCheckoutPage(partnerId);
    const [paymentElementReady, setPaymentElementReady] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const { fbt } = useI18n();

    const handlePaymentConfirmClick = async () => {
      const country = countries.find(
        (x) => x.value === billingInfo.billingCountry
      );
      if (!elements || !stripe || !country) {
        onError("Payment unsuccessful");
        return;
      }

      const isPositive = quote && quote?.totalPrice > 0;

      onProcessing();

      try {
        analytics.event(
          "Checkout Submit Payment Request",
          {
            "Quote ID": quote?.id,
            "Total Price": quote?.totalPrice,
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );

        const { error } = !isPositive
          ? await stripe.confirmSetup({
              elements,
              confirmParams: {
                return_url: redirectUrl,
                payment_method_data: {
                  billing_details: {
                    name: billingInfo.fullName,
                    email: billingInfo.email,
                    phone: billingInfo.phoneNumber,
                    address: {
                      line1: billingInfo.billingStreetAddress,
                      line2: billingInfo.billingStreetAddress,
                      city: billingInfo.billingCity,
                      state: billingInfo.billingState,
                      country: country.code,
                      postal_code: billingInfo.billingPostalCode,
                    },
                  },
                },
              },
              redirect: "if_required",
            })
          : await stripe.confirmPayment({
              elements,
              confirmParams: {
                return_url: redirectUrl,
                payment_method_data: {
                  billing_details: {
                    name: billingInfo.fullName,
                    email: billingInfo.email,
                    phone: billingInfo.phoneNumber,
                    address: {
                      line1: billingInfo.billingStreetAddress,
                      line2: billingInfo.billingStreetAddress,
                      city: billingInfo.billingCity,
                      state: billingInfo.billingState,
                      country: country.code,
                      postal_code: billingInfo.billingPostalCode,
                    },
                  },
                },
              },
              redirect: "if_required",
            });

        if (error) {
          const errorMsg = error.message || "Payment unsuccessful";
          onError(errorMsg);
          return;
        }

        onSuccess();
      } catch (error: any) {
        onError(error.message || "Payment unsuccessful");
      }
    };

    useImperativeHandle(ref, () => {
      return {
        submitPayment: handlePaymentConfirmClick,
      };
    });

    const handleChange = useCallback(
      (event: stripeJs.StripePaymentElementChangeEvent) => {
        onChangePaymentType(event.value.type);
        if (isGoogleApplePayEmbedInCheckoutPage) {
          setFieldValue("paymentComplete", event.complete);
        }
      },
      [setFieldValue, isGoogleApplePayEmbedInCheckoutPage, onChangePaymentType]
    );

    return (
      <div>
        <Container center>
          {!paymentElementReady && <PulseLoader />}
          <Spacer size={16} />
        </Container>
        <PaymentElement
          onChange={handleChange}
          onReady={() => setPaymentElementReady(true)}
          options={{
            fields: {
              billingDetails: "never",
            },
          }}
        />
        {showConfirmButton && (
          <>
            <Spacer size={24} />
            <div>
              <Button
                variant="primary"
                size="sm"
                onClick={handlePaymentConfirmClick}
                className="payment-cta__confirm"
                disabled={paymentProcessing || paymentSessionExpired}
                data-testid="payment-modal-confirm-btn"
              >
                <h5 className="m-0 ps-2 pe-2 pt-1">{fbt("Confirm and Pay")}</h5>
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }
);

export default PaymentElementInput;
